import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { DB } from 'src/contexts/FirebaseContext';
import { CommissionConfig, UpdateCommissionConfigResponse } from 'src/@types/financial';

// Default commission configuration
export const DEFAULT_COMMISSION_CONFIG: CommissionConfig = {
  stripeFees: {
    rate: 1.4,
    fixedFee: 0.25,
    internationalCardSurcharge: 1.2,
    chargeRecipient: 'instadrink',
  },
  serviceFees: {
    rate: 3,
    fixedFee: 0.25,
  },
  freeTransport: {
    cost: 0.25,
  },
  paidTransport: {
    commission: 10,
    fixedFee: 0.25,
    idShare: 8.5,
  },
  classicTicket: {
    commission: 4.5,
    fixedFee: 0.25,
    idShare: 3,
  },
  passCulture: {
    commission: 3,
    idShare: 3,
  },
};

export const financialApi = createApi({
  reducerPath: 'financialApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['CommissionConfig'],
  endpoints: (builder) => ({
    // Get commission config for an event
    getCommissionConfig: builder.query<CommissionConfig, string>({
      async queryFn(eventId) {
        try {
          const commissionConfigRef = doc(DB, 'events', eventId, 'commissionConfig', 'config');
          const commissionConfigDoc = await getDoc(commissionConfigRef);

          if (commissionConfigDoc.exists()) {
            return { data: commissionConfigDoc.data() as CommissionConfig };
          }

          // Return default config if not found
          return { data: DEFAULT_COMMISSION_CONFIG };
        } catch (error: any) {
          return { error: error.message };
        }
      },
      providesTags: (result, error, eventId) => [{ type: 'CommissionConfig', id: eventId }],
    }),

    // Update commission config for an event
    updateCommissionConfig: builder.mutation<
      UpdateCommissionConfigResponse,
      { eventId: string; commissionConfig: CommissionConfig }
    >({
      async queryFn({ eventId, commissionConfig }) {
        try {
          const commissionConfigRef = doc(DB, 'events', eventId, 'commissionConfig', 'config');

          await setDoc(
            commissionConfigRef,
            {
              ...commissionConfig,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          return {
            data: {
              success: true,
              message: 'Commission config updated successfully',
            },
          };
        } catch (error: any) {
          return {
            error: error.message,
          };
        }
      },
      invalidatesTags: (result, error, { eventId }) => [{ type: 'CommissionConfig', id: eventId }],
    }),
  }),
});

export const { useGetCommissionConfigQuery, useUpdateCommissionConfigMutation } = financialApi;
